import React from "react";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { useCurrentUser } from "../../modules/authentication/useCurrentUser";
import { useNavigate } from "react-router-dom";
import { UserRole } from "@modules/authentication/roles";
import { TextDialogWithActions } from "../../components/dialog/Dialog";
import { Routes } from "~/routes";
import { Button } from "~/components/ui/button";
import {
  useDuplicatedLabResultsCountQuery,
  useDedupeLabResultsMutation,
} from "generated/hooks";

const DebugPage = () => {
  const { loading, currentUser, cachedRole } = useCurrentUser();
  const [showWarning, setShowWarning] = React.useState(true);
  const navigate = useNavigate();

  const {
    data: countData,
    loading: countLoading,
    refetch: refetchCount,
  } = useDuplicatedLabResultsCountQuery();

  const [dedupeLabResults, { loading: deduping }] =
    useDedupeLabResultsMutation();

  const duplicateCount = countData?.duplicatedLabResultsCount ?? 0;

  const handleDedupe = async () => {
    await dedupeLabResults();
    await refetchCount();
  };

  React.useEffect(() => {
    if (!loading && currentUser?.role !== UserRole.SuperAdmin) {
      navigate(Routes.Home);
    }
  }, [loading, currentUser, navigate]);

  React.useEffect(() => {
    if (cachedRole !== UserRole.SuperAdmin) {
      navigate(Routes.Home);
    }
  }, [cachedRole, navigate]);

  if (loading) {
    return null;
  }

  return (
    <PageTemplate
      title="Debug"
      activeItem="admin"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "Debug", path: "/admin/debug" },
      ]}
    >
      <TextDialogWithActions
        actions={[
          {
            text: "Go back",
            action: () => navigate(Routes.Home),
            grey: true,
            variant: "secondary",
          },
          {
            text: "Continue",
            action: () => setShowWarning(false),
            color: "error",
          },
        ]}
        title="Warning!"
        subtitle="This page contains potentially dangerous functions that should only be used by developers. You probably shouldn't be here. Please proceed with caution, and ask before using anything on this page."
        isOpen={showWarning}
        onClose={() => setShowWarning(false)}
      />
      <div className="space-y-6 p-4">
        <div className="rounded-lg border p-4">
          <h2 className="mb-2 text-lg font-semibold">
            Lab Result Deduplication
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Finds lab results with the same indicator, laboratory, and soil
            sample combination. Keeps the first occurrence and removes
            duplicates.
          </p>
          <p className="mb-4">
            {countLoading
              ? "Counting..."
              : `${duplicateCount} duplicate lab result${duplicateCount === 1 ? "" : "s"} found.`}
          </p>
          <Button
            onClick={handleDedupe}
            disabled={deduping || countLoading || duplicateCount === 0}
            variant="destructive"
          >
            {deduping ? "Deduplicating..." : "Deduplicate"}
          </Button>
        </div>
      </div>
    </PageTemplate>
  );
};

export default DebugPage;
